import { defineStore, storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { useRootStore } from '../rootStore';
import type { IUpdateTicket } from '@/interface/betSlip';
import {
    BetPageType, BetSlipState, BetSlipType, DisplayMode, MarketPage, MarketType, OpenTicketStatus, PlaceOrderStatus, PlayerType, PriceStyle, SportType, StatementDetailStatus,
} from '@/interface/enum';
import Api, { fireAndForget } from '@/libraries/api';
import { saveLastStake } from '@/libraries/betSlip/lastStakeStorage';
import { getMaxPayout } from '@/libraries/betSlip/payout';
import { calculateStake, isValidStake } from '@/libraries/betSlip/stake';
import useState from '@/libraries/composables/useState';
import type { IEuroOutrightTicket } from '@/libraries/euroTicket';
import { getOpenTicketErrorMessage } from '@/libraries/euroTicket';
import { getDefaultStake, isOddsClose, isPlaceHierarchyClosed } from '@/libraries/helpers/betSlipHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { getSportsBsiUrl } from '@/libraries/url';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';

export const useOutrightBetSlipStore = defineStore('outrightBetSlip', () => {
    const [betSlip, setBetSlip] = useState<IEuroOutrightTicket>(getDefaultBetSlip());
    const [stake, setStake] = useState(0);
    const [isPlacingBet, setIsPlacingBet] = useState(false);

    const isTicketExist = computed(() => (oddsId: number) => betSlip.value.id === oddsId);
    const hasTicket = computed(() => betSlip.value.id !== 0);
    const isValidBetSetting = computed(() => betSlip.value.minBet > 0 && betSlip.value.maxBet > 0);

    const { formatMoneyWithSymbolAndDecimal } = useAccountStore();

    const maxPayout = computed(() => {
        const payout = getMaxPayout(stake.value, betSlip.value.price, betSlip.value.priceStyle);

        return formatMoneyWithSymbolAndDecimal(payout);
    });

    const { setDisplayMode } = useHeaderDisplayStore();
    const { setFailedPlaceBetShowAaoBanner, setTopScreenToast, setShowAcceptAnyOddsBanner } = useRootStore();
    const { setBetSlipState, setBetSlipType } = useBetSlipStore();

    function addTicket(ticket: IEuroOutrightTicket) {
        setBetSlip(ticket);
        setBetSlipState(BetSlipState.Single);
        setBetSlipType(BetSlipType.Outright);
    }

    async function removeTicket() {
        if (hasTicket.value) {
            fireAndForget(Api.clearTicket());
        }
        setBetSlip(getDefaultBetSlip());
        setBetSlipState(BetSlipState.Closed);
        setBetSlipType(BetSlipType.None);
    }

    async function clearTicketForFlowTransition() {
        const hasCurrentTicket = hasTicket.value;
        setBetSlip(getDefaultBetSlip());
        if (!hasCurrentTicket) {
            return;
        }
        try {
            await Api.clearTicket();
        } catch (error) {
            console.error(error);
        }
    }

    function updateTicket({
        key, maxBet, minBet, oddsData, messageKey, messageData,
    }: IUpdateTicket) {
        if (betSlip.value.id !== key) {
            return;
        }

        if (maxBet !== undefined) {
            betSlip.value.maxBet = maxBet;
        }

        if (minBet !== undefined) {
            betSlip.value.minBet = minBet;
        }

        if (oddsData) {
            betSlip.value.uid = oddsData.uid;
            betSlip.value.price = oddsData.price;
            betSlip.value.priceStyle = oddsData.priceStyle;
        }

        if (messageKey) {
            window.clearTimeout(betSlip.value.message.timeoutId);
            betSlip.value.message.key = messageKey;
            betSlip.value.message.data = messageData;
            betSlip.value.message.timeoutId = window.setTimeout(() => {
                betSlip.value.message.key = null;
                betSlip.value.message.data = null;
                betSlip.value.message.timeoutId = 0;
            }, 3000);
        }
    }

    const { setBalance } = useAccountStore();
    async function openTicket(isFirstTime: boolean) {
        if (!hasTicket.value) return;

        const request = {
            oddsId: betSlip.value.id,
            option: betSlip.value.option,
            marketType: MarketType.toPollux(betSlip.value.marketType),
            sportType: SportType.toPollux(betSlip.value.sportType),
            marketPageType: betSlip.value.marketPageType,
            eventId: betSlip.value.id,
            leagueId: betSlip.value.id,
            isLive: betSlip.value.isLive,
        };

        const response = await Api.openOutrightTicket(request);
        const ticketKey = Object.keys(response.oddsInfo)[0];
        const oddsData = response.oddsInfo[ticketKey];

        const isTicketRemove = isOddsClose(oddsData.errorCode);
        if (isTicketRemove.shouldRemove) {
            setTopScreenToast({
                status: 'error',
                messageKey: isTicketRemove.message,
            });
            setTimeout(async () => {
                await removeTicket();
            }, 3000);
            return;
        }

        if (isFirstTime) {
            const minBet = response.minBet;
            const maxBet = response.maxBet;
            setStake(getDefaultStake(
                minBet,
                maxBet,
                stake.value,
            ));
        }

        setBalance(response.balance);
        updateTicket({
            key: parseInt(ticketKey.replace('-or', '')),
            oddsData: oddsData.errorCode === OpenTicketStatus.Success ? oddsData : null,
            maxBet: response.maxBet,
            minBet: response.minBet,
            messageKey: getOpenTicketErrorMessage(
                oddsData.errorCode,
                betSlip.value,
                oddsData,
                betSlip.value.priceStyle === oddsData.priceStyle,
            ),
            messageData: {
                oldOdds: formatMinDecimalPlace(betSlip.value.price, 2),
                newOdds: formatMinDecimalPlace(oddsData.price, 2),
            },
        });
    }

    const { customerInfo, balance } = storeToRefs(useAccountStore());
    async function adjustStake(ticket: IEuroOutrightTicket) {
        const stakeInfo = calculateStake(
            ticket.maxBet,
            ticket.minBet,
            balance.value,
            stake.value,
            ticket.price,
        );
        setStake(stakeInfo.stake);

        updateTicket({
            key: betSlip.value.id,
            messageKey: stakeInfo.message,
        });
    }

    const { brandName } = storeToRefs(useRootStore());
    const { placeBetDelayTime } = storeToRefs(useFeatureToggleStore());
    const { refreshBetCount } = useBetCountStore();

    async function handleSuccessfulPlaceBet(ticket: IEuroOutrightTicket, stakeValue: number, orderStatus: StatementDetailStatus) {
        setTopScreenToast({
            status: 'placeBetSuccess',
            messageKey: orderStatus === StatementDetailStatus.Running
                ? 'betSlip_placeBetConfirmed'
                : 'betSlip_placeBetBeingProcessed',
        });

        setTimeout(() => {
            refreshBetCount(false);
            platform.updateBalance();
            window.eventEmitter.emit('refreshBetList');
        }, 1000);

        await removeTicket();
        saveLastStake(stakeValue);

        if (ticket.sportType !== SportType.Cricket) {
            setShowAcceptAnyOddsBanner(true);
        }

        setTimeout(() => {
            setDisplayMode(DisplayMode.ShowAll);
        }, 600);
    }

    async function handleFailedPlaceBet(placeOrderStatus: PlaceOrderStatus, message: string, extraInfo: string) {
        if (placeOrderStatus === PlaceOrderStatus.PriceChange) {
            await openTicket(false);
            return;
        }

        if (isPlaceHierarchyClosed(placeOrderStatus)) {
            window.location.href = `${getSportsBsiUrl()}`;
            return;
        }

        if (placeOrderStatus === PlaceOrderStatus.RetryOpenTicket) {
            await openTicket(false);
            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_errorMessage_generalFailure',
            });
            return;
        }

        let messageKey = message;
        let messageData: { [key: string]: any } = {};
        const { playerType } = customerInfo.value;

        switch (placeOrderStatus) {
            case PlaceOrderStatus.MaxWinLoseLimitExceed:
                messageKey = playerType === PlayerType.B2C
                    ? 'betSlip_errorMessage_maxWinLossLimit_ContactCS'
                    : 'betSlip_errorMessage_maxWinLossLimit_ContactUpperline';
                break;
            case PlaceOrderStatus.LimitExceed: {
                const adjustedStake = parseInt(extraInfo);
                if (Number.isNaN(adjustedStake)) {
                    messageKey = 'betSlip_errorMessage_limitExceedTryLater';
                } else {
                    messageData = { alternateStake: adjustedStake };
                    setStake(adjustedStake);
                    if (Number.isNaN(adjustedStake)) {
                        throw new Error('placeBet:adjustedStake is NaN');
                    }
                }
                break;
            }
            case PlaceOrderStatus.OrderProcessing:
                messageKey = 'betSlip_errorMessage_orderProcessing';
                break;
            case PlaceOrderStatus.HierarachySuspend:
            case PlaceOrderStatus.AccountProblem:
            case PlaceOrderStatus.MemberSuspend:
                messageData = { brandName: brandName.value };
                break;
            default:
                break;
        }

        updateTicket({
            key: betSlip.value.id,
            messageKey,
            messageData,
        });
    }

    async function placeBet() {
        if (!hasTicket.value || isPlacingBet.value) {
            return;
        }

        setIsPlacingBet(true);

        try {
            const ticket = betSlip.value;

            const isValidBet = isValidStake(
                stake.value,
                ticket.maxBet,
                ticket.minBet,
                balance.value,
                ticket.price,
            );

            if (!isValidBet) {
                adjustStake(ticket);
                return;
            }

            const request = {
                leagueId: ticket.leagueId,
                outrightId: ticket.id,
                stake: stake.value,
                uid: ticket.uid,
                betPage: ticket.betPage,
                sportType: SportType.toPollux(ticket.sportType),
                isLive: ticket.isLive,
            };

            const [{
                isPlaceBetSuccess,
                placeOrderStatus,
                orderStatus,
                extraInfo,
                message,
            }] = await Promise.all([Api.placeOutrightBet(request), sleep(placeBetDelayTime.value)]);

            if (isPlaceBetSuccess) {
                await handleSuccessfulPlaceBet(ticket, stake.value, orderStatus);
            } else {
                await handleFailedPlaceBet(placeOrderStatus, message, extraInfo);
                setFailedPlaceBetShowAaoBanner(true);
            }
        } catch {
            setFailedPlaceBetShowAaoBanner(true);
        } finally {
            setIsPlacingBet(false);
        }
    }

    async function refresh(isFirstTime: boolean, isCallApi: boolean) {
        if (isCallApi) {
            await openTicket(isFirstTime);
        }
    }

    const oddsId = computed(() => betSlip.value.id);
    watch(oddsId, () => {
        refresh(true, true);
    });

    return {
        betSlip,
        isTicketExist,
        addTicket,
        removeTicket,
        clearTicketForFlowTransition,
        updateTicket,
        setBetSlip,
        stake,
        setStake,
        openTicket,
        placeBet,
        adjustStake,
        hasTicket,
        isPlacingBet,
        isValidBetSetting,
        refresh,
        maxPayout,
    };
});

function getDefaultBetSlip(): IEuroOutrightTicket {
    return {
        type: 'outright',
        id: 0,
        option: '',
        marketType: MarketType.OutRight,
        sportType: SportType.Soccer,
        marketPageType: MarketPage.Outright,
        leagueId: 0,
        isLive: false,
        betPage: BetPageType.Outright,
        price: 0,
        priceStyle: PriceStyle.Unknown,
        uid: '',
        maxBet: 0,
        minBet: 0,
        marketDisplayTypeName: '',
        eventDate: '',
        message: {
            timeoutId: 0,
            key: null,
            data: null,
        },
        eventCode: '',
        isWalkinOnly: false,
        leagueName: [],
        teamName: [],
    };
}
